// ConstruireJsonAdmin.jsx - Constructeur de JSON pour formation - Design épuré (tons ambre/indigo)
import React, { useState, useCallback } from 'react';
import {
    Plus, Trash2, ChevronDown, ChevronRight,
    FileText, Layers, FileQuestion,
    Copy, AlertCircle, Save, Eye, X,
    BookOpen, GraduationCap, Award,
    Video, File, Code, Type, ListChecks
} from 'lucide-react';

// ============================================================
// TYPES DE CONTENU POUR LES LECONS
// ============================================================
const CONTENT_TYPES = [
    { value: 'VIDEO', label: 'Vidéo', icon: '🎬' },
    { value: 'TEXT_DOCUMENT', label: 'Document texte (HTML)', icon: '📄' },
    { value: 'TEXT_SIMPLE', label: 'Texte simple', icon: '📝' },
    { value: 'TEXT_ENRICHI', label: 'Texte enrichi', icon: '📖' },
    { value: 'CODE_INTERACTIF', label: 'Code interactif', icon: '💻' },
];

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
export default function ConstruireJsonAdmin({
    initialJson = null,
    onSave,
    onPreview,
    token,
    moduleId
}) {
    // ============================================================
    // 1. ÉTATS
    // ============================================================
    const [jsonData, setJsonData] = useState(() => {
        if (initialJson) {
            return typeof initialJson === 'string' ? JSON.parse(initialJson) : initialJson;
        }
        return getDefaultFormation();
    });

    const [expandedModules, setExpandedModules] = useState({});
    const [expandedLecons, setExpandedLecons] = useState({});
    const [showJsonPreview, setShowJsonPreview] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [importJson, setImportJson] = useState('');
    const [importError, setImportError] = useState(null);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [loading, setLoading] = useState(false);

    // ============================================================
    // 2. FONCTIONS UTILES
    // ============================================================
    function getDefaultFormation() {
        return {
            id: `form-${Date.now()}`,
            titre: 'Nouvelle formation',
            description: 'Description de la formation',
            categorie: 'Développement',
            niveau: 'Intermédiaire',
            dureeEstimee: '10 heures',
            langue: 'fr',
            actif: true,
            metadata: {
                version: '1.0',
                auteur: 'Équipe Pédagogique',
                derniereMiseAJour: new Date().toISOString()
            },
            modules: [],
            FinFormation: {
                titreResume: 'Félicitations !',
                aRetenir: 'Vous avez terminé la formation avec succès.'
            }
        };
    }

    function generateId(prefix) {
        return `${prefix}-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
    }

    const getContentTypeLabel = (type) => {
        const found = CONTENT_TYPES.find(t => t.value === type);
        return found ? found.label : type;
    };

    const getContentTypeIcon = (type) => {
        const found = CONTENT_TYPES.find(t => t.value === type);
        return found ? found.icon : '📄';
    };

    // ============================================================
    // 3. GESTION DES MODULES
    // ============================================================
    const addModule = useCallback(() => {
        const newModule = {
            id: generateId('mod'),
            ordre: jsonData.modules.length + 1,
            titre: `Module ${jsonData.modules.length + 1}`,
            description: 'Description du module',
            lecons: [],
            evaluations: []
        };
        setJsonData(prev => ({
            ...prev,
            modules: [...prev.modules, newModule]
        }));
        setExpandedModules(prev => ({ ...prev, [newModule.id]: true }));
    }, [jsonData]);

    const removeModule = useCallback((moduleId) => {
        if (!window.confirm('Supprimer ce module ?')) return;
        setJsonData(prev => ({
            ...prev,
            modules: prev.modules.filter(m => m.id !== moduleId)
        }));
        setExpandedModules(prev => {
            const newState = { ...prev };
            delete newState[moduleId];
            return newState;
        });
    }, []);

    const updateModule = useCallback((moduleId, field, value) => {
        setJsonData(prev => ({
            ...prev,
            modules: prev.modules.map(m =>
                m.id === moduleId ? { ...m, [field]: value } : m
            )
        }));
    }, []);

    const toggleModuleExpand = useCallback((moduleId) => {
        setExpandedModules(prev => ({
            ...prev,
            [moduleId]: !prev[moduleId]
        }));
    }, []);

    // ============================================================
    // 4. GESTION DES LECONS
    // ============================================================
    const addLecon = useCallback((moduleId) => {
        const module = jsonData.modules.find(m => m.id === moduleId);
        if (!module) return;

        const newLecon = {
            id: generateId('lec'),
            ordre: module.lecons.length + 1,
            titre: `Leçon ${module.lecons.length + 1}`,
            typeContenu: 'VIDEO',
            contenu: {
                urlVideo: '',
                dureeSec: 300,
                resume: ''
            },
            resumeLecon: {
                titreResume: `Résumé de la leçon ${module.lecons.length + 1}`,
                aRetenir: 'Points clés à retenir...'
            }
        };

        setJsonData(prev => ({
            ...prev,
            modules: prev.modules.map(m =>
                m.id === moduleId ? { ...m, lecons: [...m.lecons, newLecon] } : m
            )
        }));

        setExpandedLecons(prev => ({ ...prev, [newLecon.id]: true }));
    }, [jsonData]);

    const removeLecon = useCallback((moduleId, leconId) => {
        if (!window.confirm('Supprimer cette leçon ?')) return;
        setJsonData(prev => ({
            ...prev,
            modules: prev.modules.map(m =>
                m.id === moduleId ? { ...m, lecons: m.lecons.filter(l => l.id !== leconId) } : m
            )
        }));
        setExpandedLecons(prev => {
            const newState = { ...prev };
            delete newState[leconId];
            return newState;
        });
    }, []);

    const updateLecon = useCallback((moduleId, leconId, field, value) => {
        setJsonData(prev => ({
            ...prev,
            modules: prev.modules.map(m =>
                m.id === moduleId ? {
                    ...m,
                    lecons: m.lecons.map(l =>
                        l.id === leconId ? { ...l, [field]: value } : l
                    )
                } : m
            )
        }));
    }, []);

    const updateLeconContenu = useCallback((moduleId, leconId, field, value) => {
        setJsonData(prev => ({
            ...prev,
            modules: prev.modules.map(m =>
                m.id === moduleId ? {
                    ...m,
                    lecons: m.lecons.map(l =>
                        l.id === leconId ? {
                            ...l,
                            contenu: { ...l.contenu, [field]: value }
                        } : l
                    )
                } : m
            )
        }));
    }, []);

    const updateLeconResume = useCallback((moduleId, leconId, field, value) => {
        setJsonData(prev => ({
            ...prev,
            modules: prev.modules.map(m =>
                m.id === moduleId ? {
                    ...m,
                    lecons: m.lecons.map(l =>
                        l.id === leconId ? {
                            ...l,
                            resumeLecon: { ...l.resumeLecon, [field]: value }
                        } : l
                    )
                } : m
            )
        }));
    }, []);

    const toggleLeconExpand = useCallback((leconId) => {
        setExpandedLecons(prev => ({
            ...prev,
            [leconId]: !prev[leconId]
        }));
    }, []);

    // ============================================================
    // 5. CHANGEMENT DE TYPE DE CONTENU
    // ============================================================
    const changeLeconType = useCallback((moduleId, leconId, newType) => {
        let defaultContenu = {};
        switch (newType) {
            case 'VIDEO':
                defaultContenu = { urlVideo: '', dureeSec: 300, resume: '' };
                break;
            case 'TEXT_DOCUMENT':
            case 'TEXTE_DOCUMENT':
                defaultContenu = { corpsTexte: '', fichiersJoints: [] };
                break;
            case 'TEXT_SIMPLE':
            case 'TEXT':
                defaultContenu = { corpsTexte: '' };
                break;
            case 'TEXT_ENRICHI':
                defaultContenu = { titre: '', corpsTexte: '' };
                break;
            case 'CODE_INTERACTIF':
                defaultContenu = { snippetCode: '', langage: 'javascript', instructions: '' };
                break;
            default:
                defaultContenu = {};
        }

        setJsonData(prev => ({
            ...prev,
            modules: prev.modules.map(m =>
                m.id === moduleId ? {
                    ...m,
                    lecons: m.lecons.map(l =>
                        l.id === leconId ? {
                            ...l,
                            typeContenu: newType,
                            contenu: defaultContenu
                        } : l
                    )
                } : m
            )
        }));
    }, []);

    // ============================================================
    // 6. GESTION DES EVALUATIONS (similaire à ConstruireEvaluation)
    // ============================================================
    const addEvaluation = useCallback((moduleId) => {
        const module = jsonData.modules.find(m => m.id === moduleId);
        if (!module) return;

        const newEvaluation = {
            id: generateId('eval'),
            titre: `Évaluation ${module.evaluations.length + 1}`,
            scoreMinimumPourValider: 70,
            questions: [
                {
                    enonce: 'Question 1',
                    typeQuestion: 'CHOIX_UNIQUE',
                    options: [
                        { id: `opt-${Date.now()}-a`, texte: 'Option A' },
                        { id: `opt-${Date.now()}-b`, texte: 'Option B' },
                        { id: `opt-${Date.now()}-c`, texte: 'Option C' }
                    ],
                    resultats: ''
                }
            ]
        };

        setJsonData(prev => ({
            ...prev,
            modules: prev.modules.map(m =>
                m.id === moduleId ? { ...m, evaluations: [...m.evaluations, newEvaluation] } : m
            )
        }));
    }, [jsonData]);

    const removeEvaluation = useCallback((moduleId, evalIndex) => {
        if (!window.confirm('Supprimer cette évaluation ?')) return;
        setJsonData(prev => ({
            ...prev,
            modules: prev.modules.map(m =>
                m.id === moduleId ? {
                    ...m,
                    evaluations: m.evaluations.filter((_, idx) => idx !== evalIndex)
                } : m
            )
        }));
    }, []);

    const updateEvaluation = useCallback((moduleId, evalIndex, field, value) => {
        setJsonData(prev => ({
            ...prev,
            modules: prev.modules.map(m =>
                m.id === moduleId ? {
                    ...m,
                    evaluations: m.evaluations.map((e, idx) =>
                        idx === evalIndex ? { ...e, [field]: value } : e
                    )
                } : m
            )
        }));
    }, []);

    // Gestion des questions d'évaluation
    const addQuestion = useCallback((moduleId, evalIndex) => {
        setJsonData(prev => ({
            ...prev,
            modules: prev.modules.map(m =>
                m.id === moduleId ? {
                    ...m,
                    evaluations: m.evaluations.map((e, idx) =>
                        idx === evalIndex ? {
                            ...e,
                            questions: [
                                ...e.questions,
                                {
                                    enonce: `Question ${e.questions.length + 1}`,
                                    typeQuestion: 'CHOIX_UNIQUE',
                                    options: [
                                        { id: `opt-${Date.now()}-a`, texte: 'Option A' },
                                        { id: `opt-${Date.now()}-b`, texte: 'Option B' }
                                    ],
                                    resultats: ''
                                }
                            ]
                        } : e
                    )
                } : m
            )
        }));
    }, []);

    const removeQuestion = useCallback((moduleId, evalIndex, qIndex) => {
        if (!window.confirm('Supprimer cette question ?')) return;
        setJsonData(prev => ({
            ...prev,
            modules: prev.modules.map(m =>
                m.id === moduleId ? {
                    ...m,
                    evaluations: m.evaluations.map((e, idx) =>
                        idx === evalIndex ? {
                            ...e,
                            questions: e.questions.filter((_, qi) => qi !== qIndex)
                        } : e
                    )
                } : m
            )
        }));
    }, []);

    const updateQuestion = useCallback((moduleId, evalIndex, qIndex, field, value) => {
        setJsonData(prev => ({
            ...prev,
            modules: prev.modules.map(m =>
                m.id === moduleId ? {
                    ...m,
                    evaluations: m.evaluations.map((e, idx) =>
                        idx === evalIndex ? {
                            ...e,
                            questions: e.questions.map((q, qi) =>
                                qi === qIndex ? { ...q, [field]: value } : q
                            )
                        } : e
                    )
                } : m
            )
        }));
    }, []);

    const updateQuestionOption = useCallback((moduleId, evalIndex, qIndex, optIndex, field, value) => {
        setJsonData(prev => ({
            ...prev,
            modules: prev.modules.map(m =>
                m.id === moduleId ? {
                    ...m,
                    evaluations: m.evaluations.map((e, idx) =>
                        idx === evalIndex ? {
                            ...e,
                            questions: e.questions.map((q, qi) =>
                                qi === qIndex ? {
                                    ...q,
                                    options: q.options.map((o, oi) =>
                                        oi === optIndex ? { ...o, [field]: value } : o
                                    )
                                } : q
                            )
                        } : e
                    )
                } : m
            )
        }));
    }, []);

    const addQuestionOption = useCallback((moduleId, evalIndex, qIndex) => {
        setJsonData(prev => ({
            ...prev,
            modules: prev.modules.map(m =>
                m.id === moduleId ? {
                    ...m,
                    evaluations: m.evaluations.map((e, idx) =>
                        idx === evalIndex ? {
                            ...e,
                            questions: e.questions.map((q, qi) =>
                                qi === qIndex ? {
                                    ...q,
                                    options: [
                                        ...q.options,
                                        { id: `opt-${Date.now()}-${q.options.length + 1}`, texte: `Option ${q.options.length + 1}` }
                                    ]
                                } : q
                            )
                        } : e
                    )
                } : m
            )
        }));
    }, []);

    const removeQuestionOption = useCallback((moduleId, evalIndex, qIndex, optIndex) => {
        const module = jsonData.modules.find(m => m.id === moduleId);
        const evaluation = module?.evaluations?.[evalIndex];
        const question = evaluation?.questions?.[qIndex];
        if (question?.options?.length <= 2) {
            alert('Une question doit avoir au moins 2 options');
            return;
        }
        setJsonData(prev => ({
            ...prev,
            modules: prev.modules.map(m =>
                m.id === moduleId ? {
                    ...m,
                    evaluations: m.evaluations.map((e, idx) =>
                        idx === evalIndex ? {
                            ...e,
                            questions: e.questions.map((q, qi) =>
                                qi === qIndex ? {
                                    ...q,
                                    options: q.options.filter((_, oi) => oi !== optIndex)
                                } : q
                            )
                        } : e
                    )
                } : m
            )
        }));
    }, [jsonData]);

    // ============================================================
    // 7. FIN DE FORMATION
    // ============================================================
    const updateFinFormation = useCallback((field, value) => {
        setJsonData(prev => ({
            ...prev,
            FinFormation: { ...prev.FinFormation, [field]: value }
        }));
    }, []);

    // ============================================================
    // 8. VALIDATION, SAUVEGARDE, IMPORT/EXPORT
    // ============================================================
    function saveJsonToServer(jsonString) {
        return fetch(`/api/v1/admin/modules/${moduleId}/json-content`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: jsonString
        })
        .then(response => {
            if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);
            console.log('✅ JSON sauvegardé sur le serveur');
            return response;
        })
        .catch(err => {
            console.error('❌ Erreur sauvegarde serveur:', err);
            throw err;
        });
    }

    const validateFormation = useCallback(() => {
        const errors = [];

        if (!jsonData.titre || jsonData.titre.trim() === '') {
            errors.push('Le titre de la formation est requis');
        }
        if (!jsonData.modules || jsonData.modules.length === 0) {
            errors.push('Au moins un module est requis');
        }

        jsonData.modules.forEach((mod, idx) => {
            if (!mod.titre || mod.titre.trim() === '') {
                errors.push(`Le module ${idx + 1} doit avoir un titre`);
            }
            if (!mod.lecons || mod.lecons.length === 0) {
                errors.push(`Le module "${mod.titre || idx + 1}" doit avoir au moins une leçon`);
            }
            mod.lecons.forEach((lec, li) => {
                if (!lec.titre || lec.titre.trim() === '') {
                    errors.push(`La leçon ${li + 1} du module "${mod.titre}" doit avoir un titre`);
                }
                const contenu = lec.contenu || {};
                switch (lec.typeContenu) {
                    case 'VIDEO':
                        if (!contenu.urlVideo) errors.push(`Leçon "${lec.titre}" : URL vidéo requise`);
                        break;
                    case 'TEXT_DOCUMENT':
                    case 'TEXT_SIMPLE':
                    case 'TEXT_ENRICHI':
                        if (!contenu.corpsTexte) errors.push(`Leçon "${lec.titre}" : corps de texte requis`);
                        break;
                    case 'CODE_INTERACTIF':
                        if (!contenu.snippetCode) errors.push(`Leçon "${lec.titre}" : code source requis`);
                        break;
                    default:
                        break;
                }
            });
        });

        return errors;
    }, [jsonData]);

    const handleSave = useCallback(() => {
        const errors = validateFormation();
        if (errors.length > 0) {
            setError(errors.join('\n'));
            alert(errors.join('\n'));
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        const jsonString = JSON.stringify(jsonData, null, 2);
        if (onSave) {
            onSave(jsonString, jsonData);
            setLoading(false);
        } else if (token && moduleId) {
            saveJsonToServer(jsonString)
                .then(() => {
                    setSuccess('✅ JSON de formation sauvegardé avec succès');
                    setLoading(false);
                })
                .catch(err => {
                    setError('❌ Erreur lors de la sauvegarde: ' + err.message);
                    setLoading(false);
                });
        } else {
            setError('❌ Aucun mécanisme de sauvegarde disponible');
            setLoading(false);
        }
    }, [jsonData, onSave, validateFormation, token, moduleId]);

    const handlePreview = useCallback(() => {
        const errors = validateFormation();
        if (errors.length > 0) {
            setError(errors.join('\n'));
            return;
        }
        if (onPreview) {
            onPreview(jsonData);
        }
    }, [jsonData, onPreview, validateFormation]);

    // ============================================================
    // 9. IMPORT / EXPORT JSON
    // ============================================================
    const handleExportJson = () => {
        const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `formation-${jsonData.titre || 'export'}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setSuccess('✅ Formation exportée en JSON');
        setTimeout(() => setSuccess(null), 3000);
    };

    const handleImportJson = () => {
        try {
            const parsed = JSON.parse(importJson);
            if (!parsed.titre || !parsed.modules || !Array.isArray(parsed.modules)) {
                throw new Error('Le JSON doit contenir "titre" et "modules" (tableau).');
            }

            parsed.modules = parsed.modules.map((mod, modIdx) => {
                if (!mod.id) mod.id = generateId('mod');
                if (!mod.ordre) mod.ordre = modIdx + 1;

                mod.lecons = (mod.lecons || []).map((lec, lecIdx) => {
                    if (!lec.id) lec.id = generateId('lec');
                    if (!lec.ordre) lec.ordre = lecIdx + 1;
                    if (!lec.typeContenu) lec.typeContenu = 'VIDEO';
                    if (!lec.contenu) lec.contenu = {};
                    if (!lec.resumeLecon) lec.resumeLecon = { titreResume: '', aRetenir: '' };
                    return lec;
                });

                mod.evaluations = (mod.evaluations || []).map((evalItem, evalIdx) => {
                    if (!evalItem.id) evalItem.id = generateId('eval');
                    if (!evalItem.titre) evalItem.titre = `Évaluation ${evalIdx + 1}`;
                    if (!evalItem.scoreMinimumPourValider) evalItem.scoreMinimumPourValider = 70;
                    evalItem.questions = (evalItem.questions || []).map((q, qIdx) => {
                        if (!q.enonce) q.enonce = `Question ${qIdx + 1}`;
                        if (!q.typeQuestion) q.typeQuestion = 'CHOIX_UNIQUE';
                        q.options = (q.options || []).map(opt => {
                            if (typeof opt === 'string') {
                                return { id: generateId('opt'), texte: opt };
                            }
                            if (!opt.id) opt.id = generateId('opt');
                            return { id: opt.id, texte: opt.texte || '' };
                        });
                        if (!q.resultats) q.resultats = '';
                        return q;
                    });
                    return evalItem;
                });

                return mod;
            });

            if (!parsed.FinFormation) {
                parsed.FinFormation = { titreResume: 'Félicitations !', aRetenir: 'Vous avez terminé la formation.' };
            }

            setJsonData(parsed);
            setShowImportModal(false);
            setImportJson('');
            setImportError(null);
            setSuccess('✅ JSON importé avec succès. N\'oubliez pas de sauvegarder !');
            setTimeout(() => setSuccess(null), 4000);
        } catch (err) {
            setImportError(err.message);
        }
    };

    // ============================================================
    // 10. RENDU DES COMPOSANTS
    // ============================================================
    const renderLeconContentForm = useCallback((moduleId, leconId, typeContenu, contenu) => {
        switch (typeContenu) {
            case 'VIDEO':
                return (
                    <div className="space-y-2">
                        <div>
                            <label className="block text-xs text-indigo-700 font-serif">URL Vidéo *</label>
                            <input
                                type="text"
                                value={contenu.urlVideo || ''}
                                onChange={(e) => updateLeconContenu(moduleId, leconId, 'urlVideo', e.target.value)}
                                className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                placeholder="https://..."
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-indigo-700 font-serif">Durée (secondes)</label>
                            <input
                                type="number"
                                value={contenu.dureeSec || 300}
                                onChange={(e) => updateLeconContenu(moduleId, leconId, 'dureeSec', parseInt(e.target.value) || 0)}
                                className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-indigo-700 font-serif">Résumé</label>
                            <textarea
                                value={contenu.resume || ''}
                                onChange={(e) => updateLeconContenu(moduleId, leconId, 'resume', e.target.value)}
                                className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                rows="2"
                            />
                        </div>
                    </div>
                );

            case 'TEXT_DOCUMENT':
            case 'TEXTE_DOCUMENT':
                return (
                    <div className="space-y-2">
                        <div>
                            <label className="block text-xs text-indigo-700 font-serif">Corps du texte (HTML) *</label>
                            <textarea
                                value={contenu.corpsTexte || ''}
                                onChange={(e) => updateLeconContenu(moduleId, leconId, 'corpsTexte', e.target.value)}
                                className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                rows="4"
                                placeholder="<h1>Texte formaté</h1><p>...</p>"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-indigo-700 font-serif">Fichiers joints (JSON)</label>
                            <textarea
                                value={JSON.stringify(contenu.fichiersJoints || [], null, 2)}
                                onChange={(e) => {
                                    try {
                                        const parsed = JSON.parse(e.target.value);
                                        updateLeconContenu(moduleId, leconId, 'fichiersJoints', parsed);
                                    } catch (err) {
                                        console.error(err);
                                    }
                                }}
                                className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                rows="3"
                                placeholder='[{"nom":"fichier.pdf","url":"https://...","tailleKo":100}]'
                            />
                        </div>
                    </div>
                );

            case 'TEXT_SIMPLE':
            case 'TEXT':
                return (
                    <div>
                        <label className="block text-xs text-indigo-700 font-serif">Texte *</label>
                        <textarea
                            value={contenu.corpsTexte || ''}
                            onChange={(e) => updateLeconContenu(moduleId, leconId, 'corpsTexte', e.target.value)}
                            className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            rows="4"
                        />
                    </div>
                );

            case 'TEXT_ENRICHI':
                return (
                    <div className="space-y-2">
                        <div>
                            <label className="block text-xs text-indigo-700 font-serif">Titre</label>
                            <input
                                type="text"
                                value={contenu.titre || ''}
                                onChange={(e) => updateLeconContenu(moduleId, leconId, 'titre', e.target.value)}
                                className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-indigo-700 font-serif">Corps (HTML) *</label>
                            <textarea
                                value={contenu.corpsTexte || ''}
                                onChange={(e) => updateLeconContenu(moduleId, leconId, 'corpsTexte', e.target.value)}
                                className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                rows="4"
                            />
                        </div>
                    </div>
                );

            case 'CODE_INTERACTIF':
                return (
                    <div className="space-y-2">
                        <div>
                            <label className="block text-xs text-indigo-700 font-serif">Code *</label>
                            <textarea
                                value={contenu.snippetCode || ''}
                                onChange={(e) => updateLeconContenu(moduleId, leconId, 'snippetCode', e.target.value)}
                                className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                rows="6"
                                placeholder="// Votre code ici..."
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-indigo-700 font-serif">Langage</label>
                            <input
                                type="text"
                                value={contenu.langage || 'javascript'}
                                onChange={(e) => updateLeconContenu(moduleId, leconId, 'langage', e.target.value)}
                                className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-indigo-700 font-serif">Instructions</label>
                            <textarea
                                value={contenu.instructions || ''}
                                onChange={(e) => updateLeconContenu(moduleId, leconId, 'instructions', e.target.value)}
                                className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                rows="2"
                            />
                        </div>
                    </div>
                );

            default:
                return (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                        Type de contenu non reconnu: {typeContenu}
                    </div>
                );
        }
    }, [updateLeconContenu]);

    const renderQuestionForm = useCallback((moduleId, evalIndex, question, qIndex) => {
        return (
            <div key={qIndex} className="p-3 bg-indigo-50 rounded-lg border border-indigo-200 space-y-2">
                <div className="flex items-center justify-between">
                    <span className="text-sm font-serif font-semibold text-indigo-800">
                        Question {qIndex + 1}
                    </span>
                    <button
                        onClick={() => removeQuestion(moduleId, evalIndex, qIndex)}
                        className="p-1 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                    >
                        <Trash2 size={14} />
                    </button>
                </div>

                <div>
                    <label className="block text-xs text-indigo-700 font-serif">Énoncé *</label>
                    <input
                        type="text"
                        value={question.enonce || ''}
                        onChange={(e) => updateQuestion(moduleId, evalIndex, qIndex, 'enonce', e.target.value)}
                        className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                </div>

                <div>
                    <label className="block text-xs text-indigo-700 font-serif">Type de question</label>
                    <select
                        value={question.typeQuestion || 'CHOIX_UNIQUE'}
                        onChange={(e) => updateQuestion(moduleId, evalIndex, qIndex, 'typeQuestion', e.target.value)}
                        className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    >
                        <option value="CHOIX_UNIQUE">Choix unique</option>
                        <option value="CHOIX_MULTIPLE">Choix multiple</option>
                    </select>
                </div>

                <div>
                    <label className="block text-xs text-indigo-700 font-serif">Résultats (IDs des réponses correctes) *</label>
                    <input
                        type="text"
                        value={question.resultats || ''}
                        onChange={(e) => updateQuestion(moduleId, evalIndex, qIndex, 'resultats', e.target.value)}
                        className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        placeholder="opt-1a ou opt-1a,opt-1b"
                    />
                    <p className="text-[10px] text-indigo-400 font-serif mt-1">
                        Pour choix unique: un seul ID. Pour choix multiple: IDs séparés par des virgules
                    </p>
                </div>

                <div>
                    <label className="block text-xs text-indigo-700 font-serif mb-1">Options *</label>
                    {question.options?.map((option, optIndex) => (
                        <div key={optIndex} className="flex items-center gap-2 mb-1">
                            <input
                                type="text"
                                value={option.texte || ''}
                                onChange={(e) => updateQuestionOption(moduleId, evalIndex, qIndex, optIndex, 'texte', e.target.value)}
                                className="flex-1 px-3 py-1 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                placeholder={`Option ${optIndex + 1}`}
                            />
                            <button
                                onClick={() => removeQuestionOption(moduleId, evalIndex, qIndex, optIndex)}
                                className="p-1 text-rose-400 hover:bg-rose-50 rounded-lg transition-colors"
                            >
                                <X size={14} />
                            </button>
                        </div>
                    ))}
                    <button
                        onClick={() => addQuestionOption(moduleId, evalIndex, qIndex)}
                        className="text-xs text-amber-600 hover:text-amber-800 font-serif flex items-center gap-1 mt-1 transition-colors"
                    >
                        <Plus size={14} /> Ajouter une option
                    </button>
                </div>
            </div>
        );
    }, [addQuestionOption, removeQuestion, removeQuestionOption, updateQuestion, updateQuestionOption]);

    // ============================================================
    // 11. RENDU PRINCIPAL (avec nouveau design)
    // ============================================================
    return (
        <div className="space-y-4">
            {/* En-tête avec couleurs ambre/indigo */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h2 className="text-xl font-serif font-bold text-indigo-800 flex items-center gap-2">
                        <BookOpen size={24} className="text-amber-600" />
                        Constructeur de Formation
                    </h2>
                    <p className="text-xs text-indigo-500 font-serif mt-1">
                        Créez une formation structurée en modules, leçons et évaluations
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <button
                        onClick={() => setShowJsonPreview(!showJsonPreview)}
                        className="px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-100 border border-indigo-300 rounded-lg transition-colors"
                    >
                        {showJsonPreview ? '📝 Éditer' : '👁️ Prévisualiser JSON'}
                    </button>
                    <button
                        onClick={handleExportJson}
                        className="px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-100 border border-indigo-300 rounded-lg transition-colors"
                    >
                        ⬇️ Exporter JSON
                    </button>
                    <button
                        onClick={() => setShowImportModal(true)}
                        className="px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-100 border border-indigo-300 rounded-lg transition-colors"
                    >
                        📥 Importer JSON
                    </button>
                    <button
                        onClick={handlePreview}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors flex items-center gap-1"
                    >
                        <Eye size={16} /> Démo
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={loading}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                    >
                        {loading ? 'Sauvegarde...' : '💾 Sauvegarder'}
                    </button>
                </div>
            </div>

            {/* Messages */}
            {error && (
                <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-rose-600 text-sm font-serif whitespace-pre-wrap">
                    {error}
                </div>
            )}
            {success && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-600 text-sm font-serif">
                    {success}
                </div>
            )}

            {/* Prévisualisation JSON */}
            {showJsonPreview && (
                <div className="border border-indigo-300 rounded-lg overflow-hidden">
                    <div className="bg-indigo-800 text-white px-4 py-2 flex justify-between items-center">
                        <span className="text-sm font-serif">JSON généré</span>
                        <button
                            onClick={() => {
                                navigator.clipboard.writeText(JSON.stringify(jsonData, null, 2));
                                setSuccess('✅ JSON copié dans le presse-papier');
                                setTimeout(() => setSuccess(null), 3000);
                            }}
                            className="text-xs text-amber-200 hover:text-white transition-colors"
                        >
                            📋 Copier
                        </button>
                    </div>
                    <pre className="p-4 bg-gray-900 text-green-400 text-xs font-mono overflow-x-auto max-h-96">
                        {JSON.stringify(jsonData, null, 2)}
                    </pre>
                </div>
            )}

            {/* Formulaire de la formation */}
            {!showJsonPreview && (
                <div className="space-y-4">
                    {/* Informations générales */}
                    <div className="border border-indigo-200 rounded-lg bg-white p-4 shadow-sm">
                        <h3 className="text-lg font-serif font-bold text-indigo-800 mb-3">
                            📋 Informations de la formation
                        </h3>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs text-indigo-700 font-serif">ID</label>
                                <input
                                    type="text"
                                    value={jsonData.id || ''}
                                    onChange={(e) => setJsonData(prev => ({ ...prev, id: e.target.value }))}
                                    className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-indigo-700 font-serif">Titre *</label>
                                <input
                                    type="text"
                                    value={jsonData.titre || ''}
                                    onChange={(e) => setJsonData(prev => ({ ...prev, titre: e.target.value }))}
                                    className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                />
                            </div>
                            <div className="col-span-2">
                                <label className="block text-xs text-indigo-700 font-serif">Description</label>
                                <textarea
                                    value={jsonData.description || ''}
                                    onChange={(e) => setJsonData(prev => ({ ...prev, description: e.target.value }))}
                                    className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                    rows="2"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-indigo-700 font-serif">Catégorie</label>
                                <input
                                    type="text"
                                    value={jsonData.categorie || ''}
                                    onChange={(e) => setJsonData(prev => ({ ...prev, categorie: e.target.value }))}
                                    className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-indigo-700 font-serif">Niveau</label>
                                <input
                                    type="text"
                                    value={jsonData.niveau || ''}
                                    onChange={(e) => setJsonData(prev => ({ ...prev, niveau: e.target.value }))}
                                    className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-indigo-700 font-serif">Durée estimée</label>
                                <input
                                    type="text"
                                    value={jsonData.dureeEstimee || ''}
                                    onChange={(e) => setJsonData(prev => ({ ...prev, dureeEstimee: e.target.value }))}
                                    className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                    placeholder="10 heures"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-indigo-700 font-serif">Langue</label>
                                <input
                                    type="text"
                                    value={jsonData.langue || 'fr'}
                                    onChange={(e) => setJsonData(prev => ({ ...prev, langue: e.target.value }))}
                                    className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-indigo-700 font-serif">Version</label>
                                <input
                                    type="text"
                                    value={jsonData.metadata?.version || '1.0'}
                                    onChange={(e) => setJsonData(prev => ({
                                        ...prev,
                                        metadata: { ...prev.metadata, version: e.target.value }
                                    }))}
                                    className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-indigo-700 font-serif">Auteur</label>
                                <input
                                    type="text"
                                    value={jsonData.metadata?.auteur || ''}
                                    onChange={(e) => setJsonData(prev => ({
                                        ...prev,
                                        metadata: { ...prev.metadata, auteur: e.target.value }
                                    }))}
                                    className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Modules */}
                    <div className="border border-indigo-200 rounded-lg bg-white p-4 shadow-sm">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-serif font-bold text-indigo-800">
                                📚 Modules ({jsonData.modules?.length || 0})
                            </h3>
                            <button
                                onClick={addModule}
                                className="px-3 py-1.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors flex items-center gap-1"
                            >
                                <Plus size={16} /> Ajouter un module
                            </button>
                        </div>

                        {jsonData.modules?.length === 0 && (
                            <div className="text-center py-6 text-indigo-400 font-serif">
                                Aucun module. Cliquez sur "Ajouter un module" pour commencer.
                            </div>
                        )}

                        {jsonData.modules?.map((module, modIndex) => (
                            <div key={module.id} className="border border-indigo-200 rounded-lg mb-3 overflow-hidden">
                                {/* En-tête du module */}
                                <div
                                    className="bg-amber-50 px-4 py-2 flex items-center justify-between cursor-pointer hover:bg-amber-100 transition-colors"
                                    onClick={() => toggleModuleExpand(module.id)}
                                >
                                    <div className="flex items-center gap-3">
                                        <Layers size={16} className="text-amber-700" />
                                        <span className="font-serif font-semibold text-indigo-800">
                                            Module {module.ordre || modIndex + 1}: {module.titre || 'Sans titre'}
                                        </span>
                                        <span className="text-xs text-indigo-500 font-serif">
                                            {module.lecons?.length || 0} leçons · {module.evaluations?.length || 0} évaluations
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); removeModule(module.id); }}
                                            className="p-1 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-lg transition-colors"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                        {expandedModules[module.id] ? <ChevronDown size={16} className="text-indigo-500" /> : <ChevronRight size={16} className="text-indigo-500" />}
                                    </div>
                                </div>

                                {/* Contenu du module */}
                                {expandedModules[module.id] && (
                                    <div className="p-4 space-y-3">
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <label className="block text-xs text-indigo-700 font-serif">Ordre</label>
                                                <input
                                                    type="number"
                                                    value={module.ordre || modIndex + 1}
                                                    onChange={(e) => updateModule(module.id, 'ordre', parseInt(e.target.value) || 0)}
                                                    className="w-full px-3 py-1 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs text-indigo-700 font-serif">Titre *</label>
                                                <input
                                                    type="text"
                                                    value={module.titre || ''}
                                                    onChange={(e) => updateModule(module.id, 'titre', e.target.value)}
                                                    className="w-full px-3 py-1 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                                />
                                            </div>
                                            <div className="col-span-2">
                                                <label className="block text-xs text-indigo-700 font-serif">Description</label>
                                                <textarea
                                                    value={module.description || ''}
                                                    onChange={(e) => updateModule(module.id, 'description', e.target.value)}
                                                    className="w-full px-3 py-1 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                                    rows="2"
                                                />
                                            </div>
                                        </div>

                                        {/* Résumé du module */}
                                        <div className="border-t border-indigo-100 pt-2">
                                            <h4 className="text-sm font-serif font-semibold text-indigo-700 mb-2">
                                                📌 Résumé du module (optionnel)
                                            </h4>
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <label className="block text-xs text-indigo-700 font-serif">Titre du résumé</label>
                                                    <input
                                                        type="text"
                                                        value={module.resumeModule?.titreResume || ''}
                                                        onChange={(e) => updateModule(module.id, 'resumeModule', { ...module.resumeModule, titreResume: e.target.value })}
                                                        className="w-full px-3 py-1 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs text-indigo-700 font-serif">À retenir</label>
                                                    <input
                                                        type="text"
                                                        value={module.resumeModule?.aRetenir || ''}
                                                        onChange={(e) => updateModule(module.id, 'resumeModule', { ...module.resumeModule, aRetenir: e.target.value })}
                                                        className="w-full px-3 py-1 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Leçons */}
                                        <div className="border-t border-indigo-100 pt-2">
                                            <div className="flex items-center justify-between mb-2">
                                                <h4 className="text-sm font-serif font-semibold text-indigo-700">
                                                    📖 Leçons ({module.lecons?.length || 0})
                                                </h4>
                                                <button
                                                    onClick={() => addLecon(module.id)}
                                                    className="px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50 border border-indigo-300 rounded-lg transition-colors flex items-center gap-1"
                                                >
                                                    <Plus size={12} /> Ajouter
                                                </button>
                                            </div>

                                            {module.lecons?.map((lecon, lecIndex) => (
                                                <div key={lecon.id} className="border border-indigo-200 rounded-lg mb-2 overflow-hidden">
                                                    <div
                                                        className="bg-indigo-50 px-3 py-1.5 flex items-center justify-between cursor-pointer hover:bg-indigo-100 transition-colors"
                                                        onClick={() => toggleLeconExpand(lecon.id)}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-lg">{getContentTypeIcon(lecon.typeContenu)}</span>
                                                            <span className="text-sm font-serif font-semibold text-indigo-800">
                                                                Leçon {lecon.ordre || lecIndex + 1}: {lecon.titre || 'Sans titre'}
                                                            </span>
                                                            <span className="text-xs text-indigo-500">
                                                                {getContentTypeLabel(lecon.typeContenu)}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); removeLecon(module.id, lecon.id); }}
                                                                className="p-0.5 text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                                                            >
                                                                <Trash2 size={12} />
                                                            </button>
                                                            {expandedLecons[lecon.id] ? <ChevronDown size={14} className="text-indigo-500" /> : <ChevronRight size={14} className="text-indigo-500" />}
                                                        </div>
                                                    </div>

                                                    {expandedLecons[lecon.id] && (
                                                        <div className="p-3 space-y-2">
                                                            <div className="grid grid-cols-2 gap-2">
                                                                <div>
                                                                    <label className="block text-xs text-indigo-700 font-serif">Ordre</label>
                                                                    <input
                                                                        type="number"
                                                                        value={lecon.ordre || lecIndex + 1}
                                                                        onChange={(e) => updateLecon(module.id, lecon.id, 'ordre', parseInt(e.target.value) || 0)}
                                                                        className="w-full px-3 py-1 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <label className="block text-xs text-indigo-700 font-serif">Titre *</label>
                                                                    <input
                                                                        type="text"
                                                                        value={lecon.titre || ''}
                                                                        onChange={(e) => updateLecon(module.id, lecon.id, 'titre', e.target.value)}
                                                                        className="w-full px-3 py-1 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div>
                                                                <label className="block text-xs text-indigo-700 font-serif">Type de contenu</label>
                                                                <select
                                                                    value={lecon.typeContenu || 'VIDEO'}
                                                                    onChange={(e) => changeLeconType(module.id, lecon.id, e.target.value)}
                                                                    className="w-full px-3 py-1 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                                                >
                                                                    {CONTENT_TYPES.map(type => (
                                                                        <option key={type.value} value={type.value}>
                                                                            {type.icon} {type.label}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </div>

                                                            {renderLeconContentForm(module.id, lecon.id, lecon.typeContenu, lecon.contenu || {})}

                                                            <div className="border-t border-indigo-100 pt-2">
                                                                <h5 className="text-xs font-serif font-semibold text-indigo-600 mb-1">
                                                                    📝 Résumé de la leçon (optionnel)
                                                                </h5>
                                                                <div className="grid grid-cols-2 gap-2">
                                                                    <div>
                                                                        <label className="block text-xs text-indigo-600 font-serif">Titre du résumé</label>
                                                                        <input
                                                                            type="text"
                                                                            value={lecon.resumeLecon?.titreResume || ''}
                                                                            onChange={(e) => updateLeconResume(module.id, lecon.id, 'titreResume', e.target.value)}
                                                                            className="w-full px-3 py-1 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <label className="block text-xs text-indigo-600 font-serif">À retenir</label>
                                                                        <input
                                                                            type="text"
                                                                            value={lecon.resumeLecon?.aRetenir || ''}
                                                                            onChange={(e) => updateLeconResume(module.id, lecon.id, 'aRetenir', e.target.value)}
                                                                            className="w-full px-3 py-1 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}

                                            {module.lecons?.length === 0 && (
                                                <div className="text-center py-2 text-xs text-indigo-400 font-serif">
                                                    Aucune leçon. Cliquez sur "Ajouter".
                                                </div>
                                            )}
                                        </div>

                                        {/* Évaluations */}
                                        <div className="border-t border-indigo-100 pt-2">
                                            <div className="flex items-center justify-between mb-2">
                                                <h4 className="text-sm font-serif font-semibold text-indigo-700">
                                                    📝 Évaluations ({module.evaluations?.length || 0})
                                                </h4>
                                                <button
                                                    onClick={() => addEvaluation(module.id)}
                                                    className="px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50 border border-indigo-300 rounded-lg transition-colors flex items-center gap-1"
                                                >
                                                    <Plus size={12} /> Ajouter
                                                </button>
                                            </div>

                                            {module.evaluations?.map((evaluation, evalIndex) => (
                                                <div key={evalIndex} className="border border-indigo-200 rounded-lg p-3 mb-2">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <span className="text-sm font-serif font-semibold text-indigo-800">
                                                            {evaluation.titre || `Évaluation ${evalIndex + 1}`}
                                                        </span>
                                                        <button
                                                            onClick={() => removeEvaluation(module.id, evalIndex)}
                                                            className="p-1 text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-2">
                                                        <div>
                                                            <label className="block text-xs text-indigo-700 font-serif">Titre *</label>
                                                            <input
                                                                type="text"
                                                                value={evaluation.titre || ''}
                                                                onChange={(e) => updateEvaluation(module.id, evalIndex, 'titre', e.target.value)}
                                                                className="w-full px-3 py-1 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-xs text-indigo-700 font-serif">Score minimum (%)</label>
                                                            <input
                                                                type="number"
                                                                value={evaluation.scoreMinimumPourValider || 70}
                                                                onChange={(e) => updateEvaluation(module.id, evalIndex, 'scoreMinimumPourValider', parseInt(e.target.value) || 0)}
                                                                className="w-full px-3 py-1 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="mt-2">
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-xs font-serif font-semibold text-indigo-700">
                                                                Questions ({evaluation.questions?.length || 0})
                                                            </span>
                                                            <button
                                                                onClick={() => addQuestion(module.id, evalIndex)}
                                                                className="text-xs text-amber-600 hover:text-amber-800 font-serif flex items-center gap-1"
                                                            >
                                                                <Plus size={12} /> Ajouter
                                                            </button>
                                                        </div>

                                                        {evaluation.questions?.map((question, qIndex) => (
                                                            renderQuestionForm(module.id, evalIndex, question, qIndex)
                                                        ))}

                                                        {evaluation.questions?.length === 0 && (
                                                            <div className="text-center py-2 text-xs text-indigo-400 font-serif">
                                                                Aucune question. Cliquez sur "Ajouter".
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}

                                            {module.evaluations?.length === 0 && (
                                                <div className="text-center py-2 text-xs text-indigo-400 font-serif">
                                                    Aucune évaluation. Cliquez sur "Ajouter".
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Fin de formation */}
                    <div className="border border-indigo-200 rounded-lg bg-white p-4 shadow-sm">
                        <h3 className="text-lg font-serif font-bold text-indigo-800 mb-3">
                            🏁 Fin de la formation
                        </h3>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs text-indigo-700 font-serif">Titre de fin</label>
                                <input
                                    type="text"
                                    value={jsonData.FinFormation?.titreResume || ''}
                                    onChange={(e) => updateFinFormation('titreResume', e.target.value)}
                                    className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                    placeholder="Félicitations !"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-indigo-700 font-serif">Message de fin</label>
                                <input
                                    type="text"
                                    value={jsonData.FinFormation?.aRetenir || ''}
                                    onChange={(e) => updateFinFormation('aRetenir', e.target.value)}
                                    className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                    placeholder="Vous avez terminé la formation avec succès."
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modale d'import JSON */}
            {showImportModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6">
                        <div className="flex justify-between items-start mb-4">
                            <h3 className="text-lg font-serif font-bold text-indigo-800">Importer un JSON de formation</h3>
                            <button
                                onClick={() => { setShowImportModal(false); setImportError(null); setImportJson(''); }}
                                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                ✕
                            </button>
                        </div>
                        <textarea
                            value={importJson}
                            onChange={(e) => setImportJson(e.target.value)}
                            placeholder="Collez ici le JSON de votre formation..."
                            className="w-full h-64 px-3 py-2 border border-indigo-300 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        />
                        {importError && (
                            <div className="mt-2 text-rose-600 text-sm bg-rose-50 p-2 rounded-lg">
                                ❌ {importError}
                            </div>
                        )}
                        <div className="mt-4 flex justify-end gap-2">
                            <button
                                onClick={() => { setShowImportModal(false); setImportError(null); setImportJson(''); }}
                                className="px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50 border border-indigo-300 rounded-lg transition-colors"
                            >
                                Annuler
                            </button>
                            <button onClick={handleImportJson} className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors">
                                Valider et importer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}